import { useEffect, useMemo, useState, type FormEvent } from 'react';
import * as api from '../services/api';
import { FilterBar } from '../components/FilterBar';
import type { ActivityLog } from '../types/auth';

export function ActivityLogsPage() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [actorInput, setActorInput] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [resourceInput, setResourceInput] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 25;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.listActivityLogs({ action, page, limit });
        if (!cancelled) {
          setLogs(result.items);
          setTotal(result.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load activity logs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [action, page]);

  const filteredLogs = useMemo(() => {
    const actorQ = actorFilter.trim().toLowerCase();
    const resourceQ = resourceFilter.trim().toLowerCase();
    if (!actorQ && !resourceQ) return logs;
    return logs.filter((log) => {
      if (actorQ) {
        const actor = (log.actorUserId || 'system').toLowerCase();
        if (!actor.includes(actorQ)) return false;
      }
      if (resourceQ) {
        const resource = `${log.resourceType ?? ''} ${log.resourceId ?? ''}`.toLowerCase();
        if (!resource.includes(resourceQ)) return false;
      }
      return true;
    });
  }, [logs, actorFilter, resourceFilter]);

  function handleFilter(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setAction(actionInput.trim());
    setActorFilter(actorInput.trim());
    setResourceFilter(resourceInput.trim());
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <section className="admin-panel">
      <div className="panel-heading">
        <div>
          <h2>Activity logs</h2>
          <p>User-service audit trail. Secrets and tokens are never stored in these records.</p>
        </div>
        <span className="panel-meta">{total} total</span>
      </div>

      <FilterBar onSubmit={handleFilter}>
        <label>
          Action
          <input
            type="search"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            placeholder="e.g. user.login_succeeded"
          />
        </label>
        <label>
          Actor
          <input
            type="search"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            placeholder="Actor user id"
          />
        </label>
        <label>
          Resource
          <input
            type="search"
            value={resourceInput}
            onChange={(e) => setResourceInput(e.target.value)}
            placeholder="Type or id"
          />
        </label>
        <button type="submit" className="primary-btn">
          Apply
        </button>
      </FilterBar>

      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {loading ? <p className="muted">Loading logs…</p> : null}
      {!loading && filteredLogs.length === 0 ? (
        <p className="muted">No activity logs found.</p>
      ) : null}

      {filteredLogs.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Resource</th>
                <th>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}</td>
                  <td>
                    <code>{log.action}</code>
                  </td>
                  <td>
                    <code>{log.actorUserId || 'system'}</code>
                  </td>
                  <td>
                    {log.resourceType || '—'}
                    {log.resourceId ? (
                      <>
                        <br />
                        <code>{log.resourceId}</code>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <pre className="metadata-block">
                      {log.metadata ? JSON.stringify(log.metadata, null, 2) : '—'}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {totalPages > 1 ? (
        <div className="pager">
          <button
            type="button"
            className="ghost-btn"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="ghost-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
}
